import { Injectable, inject } from '@angular/core';

import {
    AppInfoConfig,
    CookieName,
    CookieService,
    DateTimeService,
    FetchApiService,
    FrontendHelperService,
    Logger,
    NativeAppService,
    NavigationService,
    UserService,
    WINDOW,
    WebWorkerService,
    WindowEvent,
    WorkerType,
} from '@frontend/vanilla/core';
import { firstValueFrom } from 'rxjs';

import { GoogleAnalyticsConfig } from './google-analytics.client-config';
import { Ga4Event, MeasurementEvent } from './google-analytics.models';

/**
 * @desc
 * Service for extracting Google Analytics cookie data.
 *
 * @whatItDoes
 * This service reads the Google Analytics cookies and extracts the client ID and session ID,
 * storing them in separate cookies for easier access.
 *
 * @experimental
 */
@Injectable({
    providedIn: 'root',
})
export class GoogleAnalyticsService {
    private readonly appInfoConfig = inject(AppInfoConfig);
    private readonly cookieService = inject(CookieService);
    private readonly dateTimeService = inject(DateTimeService);
    private readonly fetchApiService = inject(FetchApiService);
    private readonly frontendHelperService = inject(FrontendHelperService);
    private readonly googleAnalyticsConfig = inject(GoogleAnalyticsConfig);
    private readonly log = inject(Logger);
    private readonly nativeAppService = inject(NativeAppService);
    private readonly navigationService = inject(NavigationService);
    private readonly userService = inject(UserService);
    private readonly webWorkerService = inject(WebWorkerService);
    private readonly window = inject(WINDOW);

    private readonly cookiesExpiryDate: Date = this.dateTimeService.getFutureDate(1);
    private readonly googleAnalyticsApiEndpoint: string = `${this.navigationService.location.baseUrl()}/${this.navigationService.location.culture}/api/googleanalytics/track`;
    private readonly ga4Events: Ga4Event[] = [];
    private static readonly MaxEventsPerRequest: number = 25;

    constructor() {
        this.window.addEventListener(
            WindowEvent.BeforeUnload,
            () => {
                while (this.ga4Events.length > 0) {
                    const events = this.ga4Events.splice(0, GoogleAnalyticsService.MaxEventsPerRequest);
                    const blob = new Blob([JSON.stringify({ ...this.measurement, events })], {
                        type: 'application/json',
                    });

                    this.window.navigator.sendBeacon(this.googleAnalyticsApiEndpoint, blob);
                }
            },
            false,
        );
    }

    private get measurement(): MeasurementEvent {
        return {
            user_id: this.userService.accountId ?? undefined,
            timestamp_micros: this.dateTimeService.now().getTime() * 1000, // GA4 requires timestamp in microseconds
            user_properties: {
                appinfo: {
                    value: this.appInfoConfig.channel,
                },
                device_native: {
                    value: this.nativeAppService.isNative.toString(),
                },
                page_fe: {
                    value: this.frontendHelperService.getFrontendDescription(),
                },
                prof_country: {
                    value: this.userService.country?.toLowerCase(),
                },
                prof_customerid: {
                    value: this.userService.accountId ?? undefined,
                },
                prof_loggedin: {
                    value: this.userService.isAuthenticated ? 'logged_in' : 'not_logged_in',
                },
            },
            events: [],
        };
    }

    /**
     * Tracks a GA4 event by sending a measurement protocol request.
     *
     * This method constructs a MeasurementEvent object with the provided event name and parameters,
     * along with additional user and session information. It then sends this data to the specified
     * Google Analytics endpoint using the FetchApiService.
     *
     * @param event The GA4 event to track, containing the event name and parameters.
     * @returns A promise that resolves when the tracking request is completed.
     */
    track(event: Ga4Event) {
        this.ga4Events.push({
            name: event.name,
            params: {
                session_id: this.cookieService.get(CookieName.VnGoogleAnalyticsClientId),
                page_hostname: this.navigationService.location.host(),
                page_name: this.navigationService.location.path(),
                page_trackerid: this.cookieService.get(CookieName.TrackerId),
                clean_page_URL: this.navigationService.location.baseUrl() + this.navigationService.location.path(),
                prof_loggedin_event: this.measurement.user_properties.prof_loggedin.value,
                custom_timestamp: this.measurement.timestamp_micros?.toString(),
                prof_customerid_event: this.userService.accountId ?? undefined,
                ...event.params,
            },
        });

        this.webWorkerService.createWorker(
            WorkerType.TrackEventsTimeout,
            { timeout: this.googleAnalyticsConfig.isConfigReady ? this.googleAnalyticsConfig.trackEventsTimeoutInMilliseconds : 5000 },
            async () => {
                let events: Ga4Event[] = [];

                try {
                    while (this.ga4Events.length > 0) {
                        events = this.ga4Events.splice(0, GoogleAnalyticsService.MaxEventsPerRequest);
                        await firstValueFrom(
                            this.fetchApiService.request({
                                url: this.googleAnalyticsApiEndpoint,
                                data: { ...this.measurement, events },
                                method: 'POST',
                                headers: {
                                    'x-bwin-sf-api': -1, // Use host product API
                                },
                            }),
                        );
                    }
                } catch (error) {
                    this.log.errorRemote(`Failed to track GA4 events: ${JSON.stringify(events)}`, error);
                    this.webWorkerService.removeWorker(WorkerType.TrackEventsTimeout);
                }

                this.webWorkerService.removeWorker(WorkerType.TrackEventsTimeout);
            },
        );
    }

    /**
     * @desc Updates the cookies for Google Analytics client ID and session ID.
     */
    updateCookies() {
        this.updateClientIdCookie();
        this.updateSessionIdCookie();
    }

    /**
     * Extracts the GA4 client ID from the `_ga` cookie.
     * Example: _ga=GA1.1.900335738.1755505472
     */
    private updateClientIdCookie() {
        const gaCookie = this.cookieService.get(CookieName.GoogleAnalytics);
        const gaClientId = gaCookie?.match(this.googleAnalyticsConfig.gaCookieClientIdRegex)?.[1];

        if (gaClientId) {
            this.cookieService.put(CookieName.VnGoogleAnalyticsClientId, gaClientId, { expires: this.cookiesExpiryDate });
        }
    }

    /**
     * Extracts session ID from the `_ga_<measurementId>` cookie.
     * Example: _ga_ABC123=GS2.1.s1754042772$o1$g1$t1754044274$j60$l0$h0
     */
    private updateSessionIdCookie() {
        const measurementId = this.googleAnalyticsConfig.measurementId.replace('G-', '');
        const gaSessionCookie = this.cookieService.get(`${CookieName.GoogleAnalytics}_${measurementId}`);
        const gaSessionId = gaSessionCookie?.match(this.googleAnalyticsConfig.gaCookieSessionIdRegex)?.[1];

        if (gaSessionId) {
            this.cookieService.put(CookieName.VnGoogleAnalyticsSessionId, gaSessionId, { expires: this.cookiesExpiryDate });
        }
    }
}
